import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'
import type { DocumentNode } from 'graphql'
import { AliyahType, BarMitzvahType, HaftarahType, Query, SentenceGroupType, SentenceType, TropeLineType } from 'types/types'
import type { ActionContext } from 'vuex'

import UpdateKippahReminder from '@/mutations/UpdateKippahReminder.graphql'
import UpdatePracticeTracking from '@/mutations/UpdatePracticeTracking.graphql'
import AliyahSentencesQuery from '@/queries/AliyahSentencesQuery.graphql'
import HaftarahSentencesQuery from '@/queries/HaftarahSentencesQuery.graphql'
import MyBarMitzvahQuery from '@/queries/MyBarMitzvahQuery.graphql'
import TropesQuery from '@/queries/TropesQuery.graphql'
import { ReportingMutation, runStoreMutation } from '@/store/mutationHelpers'
import { querySentenceGroupSentences } from '@/store/queryHelpers'
import { sortAliyotByRank } from '@/util/Aliyah'
import { useApollo } from '@/util/apolloClient'
import { PracticeSettings } from '@/util/PracticeSettings'
import { cloneRaw } from '@/util/util'

const { apolloClient } = useApollo()

export interface StudentState {
  barMitzvah: BarMitzvahType | null
  aliyahSentences: Record<number, Array<SentenceType>>
  haftarahSentences: Record<number, Array<SentenceType>>
  sentenceGroupSentences: Record<number, Array<SentenceType>>
  tropes: Record<string, Array<TropeLineType>>
  practiceSettings: PracticeSettings | null
  error: string | null
  loading: boolean
}

const resetState = (): StudentState => {
  return {
    aliyahSentences: {} as Record<number, Array<SentenceType>>,
    barMitzvah: null,
    error: null,
    haftarahSentences: {} as Record<number, Array<SentenceType>>,
    loading: false,
    practiceSettings: null,
    sentenceGroupSentences: {} as Record<number, Array<SentenceType>>,
    tropes: {} as Record<string, Array<TropeLineType>>,
  }
}

/** Run a bar mitzvah setting mutation and, only if it succeeded, refresh the bar mitzvah. */
async function updateBarMitzvahSetting<K extends ReportingMutation>(
  context: ActionContext<StudentState, unknown>,
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
): Promise<boolean> {
  // eslint-disable-next-line no-use-before-define
  const { dispatch } = studentActionContext(context)
  if (!(await runStoreMutation(context, mutation, variables, dataKey))) {
    return false
  }
  await dispatch.fetchBarMitzvah()
  return true
}

const student = defineModule({
  actions: {
    clearBarMitzvah(context) {
      const { commit } = studentActionContext(context)
      commit.clearBarMitzvah()
    },

    clearPracticeSettings(context) {
      const { commit } = studentActionContext(context)
      commit.clearPracticeSettings()
    },

    async fetchAliyahSentences(context, payload: { aliyah: AliyahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)

      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'aliyahSentences'>>({
          fetchPolicy: 'network-only',
          query: AliyahSentencesQuery,
          variables: {
            aliyah: payload.aliyah.id,
          },
        })
        commit.setAliyahSentences({
          aliyah: payload.aliyah,
          sentences: (response.data?.aliyahSentences?.filter(Boolean) as SentenceType[]) ?? [],
        })
      } catch (error) {
        console.error('fetchAliyahSentences failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchBarMitzvah(context): Promise<void> {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)

      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'myBarMitzvah'>>({
          fetchPolicy: 'network-only',
          query: MyBarMitzvahQuery,
        })
        if (response.data?.myBarMitzvah) {
          commit.setBarMitzvah(response.data.myBarMitzvah)
        }
      } catch {
        // No bar mitzvah record for this user (e.g. teachers without one)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchHaftarahSentences(context, payload: { haftarah: HaftarahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)

      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'haftarahSentences'>>({
          fetchPolicy: 'network-only',
          query: HaftarahSentencesQuery,
          variables: {
            haftarah: payload.haftarah.reading.id,
          },
        })
        commit.setHaftarahSentences({
          haftarah: payload.haftarah,
          sentences: (response.data?.haftarahSentences?.filter(Boolean) as SentenceType[]) ?? [],
        })
      } catch (error) {
        console.error('fetchHaftarahSentences failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchSentenceGroupSentences(context, payload: { sentenceGroup: SentenceGroupType }): Promise<void> {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)

      commit.setLoading(true)
      try {
        commit.setSentenceGroupSentences(await querySentenceGroupSentences(payload.sentenceGroup))
      } catch (error) {
        console.error('fetchSentenceGroupSentences failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchTropes(context): Promise<void> {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)

      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'tropes'>>({
          fetchPolicy: 'network-only',
          query: TropesQuery,
        })
        response.data!.tropes?.forEach((tropeSet) => {
          if (!tropeSet) return
          commit.setTropes({
            tropeGroup: tropeSet.name,
            tropeLines: tropeSet.tropeLines,
          })
        })
      } catch (error) {
        console.error('fetchTropes failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    reset(context) {
      const { commit } = studentActionContext(context)
      commit.reset()
    },

    resetError(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)
      commit.setError(null)
    },

    setBarMitzvah(context, barMitzvah: BarMitzvahType) {
      const { commit } = studentActionContext(context)
      commit.setBarMitzvah(barMitzvah)
    },

    setPracticeSettings(context, practiceSettings: PracticeSettings) {
      const { commit } = studentActionContext(context)
      commit.setPracticeSettings(practiceSettings)
    },

    /** Toggle the kippah reminder setting and refresh bar mitzvah data. Resolves false (and shows why) on failure. */
    updateKippahReminder(context, kippahReminder: boolean): Promise<boolean> {
      return updateBarMitzvahSetting(context, UpdateKippahReminder, { kippahReminder }, 'updateKippahReminder')
    },

    updateLoading(context, loading: boolean) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = studentActionContext(context)
      commit.setLoading(loading)
    },

    /** Toggle the practice tracking setting and refresh bar mitzvah data. Resolves false (and shows why) on failure. */
    updatePracticeTracking(context, enabled: boolean): Promise<boolean> {
      return updateBarMitzvahSetting(context, UpdatePracticeTracking, { enabled }, 'updatePracticeTracking')
    },
  },

  getters: {
    aliyahSentences: (...args): Record<number, Array<SentenceType>> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.aliyahSentences
    },

    barMitzvah: (...args): BarMitzvahType | null => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.barMitzvah
    },

    /** Whether the bar mitzvah's kippah reminder setting is enabled. */
    barMitzvahKippahReminder: (...args): boolean => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.barMitzvah?.kippahReminder ?? false
    },

    /** Whether the bar mitzvah's practice tracking setting is enabled. */
    barMitzvahPracticeTracking: (...args): boolean => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.barMitzvah?.practiceTracking ?? true
    },

    error(...args): string | null {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.error
    },

    /** Look up cached aliyah sentences by GraphQL string ID (hides parseInt conversion). */
    getAliyahSentences: (...args): ((id: string) => Array<SentenceType>) => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return (id: string) => state.aliyahSentences[parseInt(id, 10)] ?? []
    },

    /** Look up cached haftarah sentences by haftarah.reading.id (hides parseInt conversion). */
    getHaftarahSentences: (...args): ((readingId: string) => Array<SentenceType>) => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return (readingId: string) => state.haftarahSentences[parseInt(readingId, 10)] ?? []
    },

    /** Look up cached sentence group sentences by sg.reading.id (hides parseInt conversion). */
    getSentenceGroupSentences: (...args): ((readingId: string) => Array<SentenceType>) => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return (readingId: string) => state.sentenceGroupSentences[parseInt(readingId, 10)] ?? []
    },

    haftarahSentences: (...args): Record<number, Array<SentenceType>> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.haftarahSentences
    },

    loading(...args): boolean {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.loading
    },

    practiceSettings: (...args): PracticeSettings | null => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.practiceSettings
    },

    sentenceGroupSentences: (...args): Record<number, Array<SentenceType>> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.sentenceGroupSentences
    },

    tropes: (...args): Record<string, Array<TropeLineType>> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = studentGetterContext(args)
      return state.tropes
    },
  },

  mutations: {
    clearBarMitzvah(state: StudentState) {
      state.barMitzvah = null
    },

    clearPracticeSettings(state: StudentState) {
      state.practiceSettings = null
    },

    reset(state: StudentState) {
      Object.assign(state, { ...resetState() })
    },

    setAliyahSentences(state: StudentState, payload: { aliyah: AliyahType; sentences: Array<SentenceType> }) {
      state.aliyahSentences[parseInt(payload.aliyah.id, 10)] = cloneRaw(payload.sentences)
    },

    setBarMitzvah(state: StudentState, barMitzvah: BarMitzvahType) {
      state.barMitzvah = cloneRaw(barMitzvah)
      state.barMitzvah.aliyot = sortAliyotByRank(state.barMitzvah.aliyot ?? [])
    },

    setError(state: StudentState, error: string | null) {
      state.error = error
    },

    setHaftarahSentences(state: StudentState, payload: { haftarah: HaftarahType; sentences: Array<SentenceType> }) {
      state.haftarahSentences[parseInt(payload.haftarah.reading.id, 10)] = cloneRaw(payload.sentences)
    },

    setLoading(state: StudentState, loading: boolean) {
      state.loading = loading
    },

    setPracticeSettings(state: StudentState, practiceSettings: PracticeSettings) {
      state.practiceSettings = cloneRaw(practiceSettings)
    },

    setSentenceGroupSentences(
      state: StudentState,
      payload: {
        sentenceGroup: SentenceGroupType
        sentences: Array<SentenceType>
      },
    ) {
      const sgId = payload.sentenceGroup.reading.id
      state.sentenceGroupSentences[parseInt(sgId, 10)] = cloneRaw(payload.sentences)
    },

    setTropes(state: StudentState, payload: { tropeGroup: string; tropeLines: Array<TropeLineType> }) {
      const { tropeGroup } = payload
      state.tropes[tropeGroup] = cloneRaw(payload.tropeLines)
    },
  },

  namespaced: true as const,

  state: resetState(),
})

export default student
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const studentGetterContext = (args: [any, any, any, any]) => localGetterContext(args, student)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const studentActionContext = (context: any) => localActionContext(context, student)
